import { EmptyState, FlexColumn, FullContainer, Loading, useLoading, useMoment } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { StyledBody, StyledCell, StyledHead, StyledHeadCell, StyledRow, StyledTable } from 'baseui/table';
import { HeadingXSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyleObject } from 'styletron-react';
import { useStyles } from '../../../hooks/useStyles';
import { getAssetDtc } from '../../../services/assetService';
import { DtcRecord } from '../../../types/currentState';
import { getStartDate } from '../../../utils/dates';
import LocalizedDatePicker from '../../shared/LocalizedDatePicker';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  assetId?: string;
};

function DtcListModal({ assetId, onClose, isOpen }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { formatDateFromTs } = useMoment();
  const descriptionCellStyle: StyleObject = { minWidth: '250px', maxWidth: '250px' };
  const dateCellStyle: StyleObject = { minWidth: '150px', maxWidth: '150px' };
  const tableStyle: StyleObject = { minWidth: '800px' };
  const [selectedDate, setSelectedDate] = useState(getStartDate());
  const [records, setRecords] = useState<DtcRecord[]>([]);

  useEffect(() => {
    if (!assetId) {
      stopLoading();
      return;
    }
    const loadDtcRecords = async () => {
      startLoading();
      const { records: apiRecords, error } = await getAssetDtc(assetId, selectedDate);
      if (!error && apiRecords) {
        setRecords(apiRecords);
      } else {
        setRecords([]);
      }
      stopLoading();
    };

    if (isOpen) {
      loadDtcRecords();
    }
  }, [assetId, isOpen, selectedDate]);

  const onDateChange = (date: Date) => {
    setSelectedDate(date.getTime());
  };

  if (!assetId) {
    return null;
  }

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="auto"
      autoFocus={false}
    >
      <ModalHeader>
        <HeadingXSmall margin={0}>Registro de fallas:</HeadingXSmall>
      </ModalHeader>
      <ModalBody>
        <FlexColumn>
          <LocalizedDatePicker
            value={[new Date(selectedDate)]}
            onChange={({ date }) => {
              if (date && !(date instanceof Array)) {
                onDateChange(date);
              }
            }}
            onClose={() => {
              onDateChange(new Date(selectedDate));
            }}
            size="compact"
            maxDate={new Date()}
          />
          <FullContainer
            verticalFlex
            classNames={css({
              minWidth: '100%',
              minHeight: '100px',
              justifyContent: 'center',
              [theme.mediaQuery.large]: {
                minWidth: 'clamp(100%, 800px, 800px)',
              },
            })}
          >
            {loading && <Loading />}
            {!loading && !records.length && <EmptyState title="No se encontraron fallas para el día seleccionado." />}
            {!loading && records.length > 0 && (
              <StyledTable className={css({ minHeight: '200px', maxHeight: '300px' })}>
                <StyledHead
                  role="row"
                  $style={tableStyle}
                >
                  <StyledHeadCell role="columnheader">Código</StyledHeadCell>
                  <StyledHeadCell
                    role="columnheader"
                    $style={descriptionCellStyle}
                  >
                    Descripción
                  </StyledHeadCell>
                  <StyledHeadCell role="columnheader">Estándar</StyledHeadCell>
                  <StyledHeadCell role="columnheader">Estatus</StyledHeadCell>
                  <StyledHeadCell
                    role="columnheader"
                    $style={dateCellStyle}
                  >
                    Fecha
                  </StyledHeadCell>
                </StyledHead>
                <StyledBody $style={tableStyle}>
                  {records.map((record, rowIndex) => (
                    <StyledRow key={rowIndex}>
                      <StyledCell>{record.code ?? '-'}</StyledCell>
                      <StyledCell $style={descriptionCellStyle}>{record.description ?? '-'}</StyledCell>
                      <StyledCell>{record.standard ?? '-'}</StyledCell>
                      <StyledCell>{record.status ?? '-'}</StyledCell>
                      <StyledCell $style={dateCellStyle}>
                        {record.timestamp ? formatDateFromTs(record.timestamp) : '-'}
                      </StyledCell>
                    </StyledRow>
                  ))}
                </StyledBody>
              </StyledTable>
            )}
          </FullContainer>
        </FlexColumn>
      </ModalBody>
    </Modal>,
    document.body
  );
}

export default memo(DtcListModal);
